import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../../utils/pieceDetachment';
import cerfaExample from '../../../assets/ExempleCerfa.pdf';
import theme from '../../../theme';
import FirebaseUploadComponent from '../../CreateCotation/FirebaseUploadComponent';
import CfnPiecesWarning from './CfnPiecesWarning';

interface UploadModalProps {
  dealId: number;
  photos: Record<string, string[]>;
  extUrl?: string;
  regPlate?: string;
}

interface DealData {
  business_type: string;
  business_type_id?: number;
  damages?: string;
}

const UploadModal: React.FC<UploadModalProps> = ({ dealId, photos, extUrl, regPlate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dealData, setDealData] = useState<DealData | null>(null);
  const [checkboxAccepted, setCheckboxAccepted] = useState(false);

  const [certificateOfTransferUrl, setCertificateOfTransferUrl] = useState<string[]>(
    photos?.certificateOfTransfer || [],
  );
  const [barredVehicleRegistrationDocumentUrl, setBarredVehicleRegistrationDocumentUrl] = useState<string[]>(
    photos?.barredVehicleRegistrationDocument || [],
  );
  const [gloveBoxDocumentsUrl, setGloveBoxDocumentsUrl] = useState<string[]>(photos?.gloveBoxDocuments || []);

  // Auto-open modal if action=openProcurementModal in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');
    const autoSearch = searchParams.get('autoSearch');

    // Only open if action is set AND (no autoSearch OR this deal matches the autoSearch)
    const shouldOpen =
      action === 'openProcurementModal' &&
      !open &&
      (!autoSearch ||
        regPlate?.toLowerCase().replace(/[^a-z0-9]/g, '') === autoSearch.toLowerCase().replace(/[^a-z0-9]/g, ''));

    if (shouldOpen) {
      console.log('📂 [UPLOAD MODAL] Auto-opening modal for deal:', {
        dealId,
        regPlate,
        autoSearch,
      });
      setOpen(true);
      // Clean up URL by removing the action parameter
      searchParams.delete('action');
      const newSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true },
      );
    }
  }, [location, navigate, open, regPlate, dealId]);

  // Fetch deal data on component mount
  useEffect(() => {
    const fetchDealData = async () => {
      try {
        const response = await makeAuthenticatedApiCall('get', `/api/deals/${dealId}/auth`);
        console.log('📥 [UPLOAD MODAL] Deal data fetched:', response.data);
        setDealData(response.data as DealData);
      } catch (error) {
        console.error('Error fetching deal data:', error);
      }
    };

    if (open) {
      fetchDealData();
    }
  }, [dealId, open]);

  // Check if business type is straight_trade_in (ID 21)
  const isStraightTradeIn = dealData?.business_type === 'straight_trade_in' || dealData?.business_type_id === 21;

  // Calculate if all required files are uploaded
  const allFilesUploaded =
    certificateOfTransferUrl.length > 0 &&
    barredVehicleRegistrationDocumentUrl.length > 0 &&
    (!isStraightTradeIn || gloveBoxDocumentsUrl.length > 0);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleUploadComplete = (url: string, category: string) => {
    if (category === 'certificateOfTransfer') {
      setCertificateOfTransferUrl((prevUrls) => [...prevUrls, url]);
    } else if (category === 'barredVehicleRegistrationDocument') {
      setBarredVehicleRegistrationDocumentUrl((prevUrls) => [...prevUrls, url]);
    } else if (category === 'gloveBoxDocuments') {
      setGloveBoxDocumentsUrl((prevUrls) => [...prevUrls, url]);
    }
  };

  // Retrait en base AVANT l'objet Storage : « supprimer puis annuler »
  // laissait sinon une URL morte dans car_deals.photos, que le worker CFN
  // n'aurait pas pu archiver. Un 404 (pièce pas encore validée) ne bloque pas.
  const handleBeforeDelete = (url: string) => requestPieceDetachment(pieceDetachmentEndpoints.deal(dealId), url);

  const handleDeleteComplete = (url: string, category: string) => {
    if (category === 'certificateOfTransfer') {
      setCertificateOfTransferUrl((prevUrls) => prevUrls.filter((existingUrl) => existingUrl !== url));
    } else if (category === 'barredVehicleRegistrationDocument') {
      setBarredVehicleRegistrationDocumentUrl((prevUrls) => prevUrls.filter((existingUrl) => existingUrl !== url));
    } else if (category === 'gloveBoxDocuments') {
      setGloveBoxDocumentsUrl((prevUrls) => prevUrls.filter((existingUrl) => existingUrl !== url));
    } else {
      console.error('Catégorie de fichier non gérée');
    }
  };

  const isValidationButtonDisabled =
    certificateOfTransferUrl?.length === 0 ||
    barredVehicleRegistrationDocumentUrl?.length === 0 ||
    (isStraightTradeIn && gloveBoxDocumentsUrl?.length === 0);

  const handleOpenConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setCheckboxAccepted(false);
  };

  const handleFinalValidation = async () => {
    setLoading(true);
    try {
      const photos: Record<string, string[]> = {
        certificateOfTransfer: certificateOfTransferUrl,
        barredVehicleRegistrationDocument: barredVehicleRegistrationDocumentUrl,
      };

      if (isStraightTradeIn) {
        photos.gloveBoxDocuments = gloveBoxDocumentsUrl;
      }

      await makeAuthenticatedApiCall('post', `/api/deals/${dealId}/update-car-deal-procurement-file`, photos);

      // Mark files as complete in database
      await makeAuthenticatedApiCall('post', `/api/deals/${dealId}/mark-procurement-complete`, {});

      // Send notification email to admins
      if (allFilesUploaded) {
        await makeAuthenticatedApiCall('post', `/api/mails/procurement-file-admin-notification`, {
          dealId: dealId,
          extUrl: extUrl,
          regPlate: regPlate,
        });
      }

      handleCloseConfirmModal();
      handleClose();
    } catch (error) {
      console.error('Erreur lors de la validation des fichiers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse damages JSON
  const parseDamages = (damagesString: string | undefined) => {
    if (!damagesString) {
      return null;
    }

    try {
      // Handle double-encoded JSON string from the database
      let damages: any = damagesString;

      // Parse if it's a string
      if (typeof damages === 'string') {
        damages = JSON.parse(damages);
      }

      // If it's still a string after first parse (double-encoded), parse again
      if (typeof damages === 'string') {
        damages = JSON.parse(damages);
      }

      // Now damages should be an array of strings like:
      // ["bouclier_avg : peinture (rayure profonde) (371 €)", ...]
      if (Array.isArray(damages)) {
        // Filter out damages with 0€ or "aucun dommage"
        const filteredDamages = damages.filter((damage: string) => {
          const lowerDamage = damage.toLowerCase();
          return (
            !lowerDamage.includes('(0 €)') &&
            !lowerDamage.includes('aucun dommage') &&
            !lowerDamage.includes('etat conforme') &&
            !lowerDamage.includes('non grêlé')
          );
        });

        return filteredDamages.length > 0 ? filteredDamages : null;
      }

      // Handle old format: {key: {amount: number}}
      if (typeof damages === 'object' && !Array.isArray(damages)) {
        const damagesList: string[] = [];
        Object.entries(damages).forEach(([key, value]) => {
          if (value && typeof value === 'object') {
            const damageValue = value as any;
            if (damageValue.amount && damageValue.amount > 0) {
              damagesList.push(`${key}: ${damageValue.amount}€`);
            }
          }
        });
        return damagesList.length > 0 ? damagesList : null;
      }

      return null;
    } catch (error) {
      console.error('Error parsing damages:', error);
      return null;
    }
  };

  const damagesList = parseDamages(dealData?.damages);

  const buttonColor = allFilesUploaded ? '#4caf50' : theme.palette.saffron.main;

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        className="uploadModalButton"
        sx={{
          backgroundColor: buttonColor,
          '&:hover': { backgroundColor: buttonColor },
          transition: 'background-color 0.3s ease',
        }}
      >
        {allFilesUploaded ? '✅' : '📤'} Dossier d'achat Skipcar{' '}
        {certificateOfTransferUrl?.length +
          barredVehicleRegistrationDocumentUrl?.length +
          gloveBoxDocumentsUrl?.length >
        0
          ? `(${
              certificateOfTransferUrl?.length +
              barredVehicleRegistrationDocumentUrl?.length +
              gloveBoxDocumentsUrl?.length
            })`
          : ''}
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
          <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
            Documents attendus pour le véhicule {regPlate}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: 2 }}>
          {/* Documents Checklist */}
          <Box
            sx={{
              backgroundColor: '#fafafa',
              borderLeft: '3px solid #1a1a1a',
              padding: '20px 24px',
              marginBottom: '24px',
              borderRadius: '4px',
            }}
          >
            <Typography
              sx={{
                margin: '0 0 20px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1a1a1a',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Récapitulatif des documents à déposer
            </Typography>

            {/* Document 1 - Certificat de cession */}
            <Box
              sx={{
                margin: '0 0 16px 0',
                padding: '16px',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: '1px solid #e5e5e5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      margin: '0 0 8px 0',
                      fontSize: '15px',
                      color: '#1a1a1a',
                      fontWeight: 600,
                    }}
                  >
                    Certificat de cession
                  </Typography>
                  <Typography
                    sx={{
                      margin: '0 0 12px 0',
                      fontSize: '14px',
                      color: '#6a6a6a',
                      lineHeight: '20px',
                    }}
                  >
                    Complété, daté et signé le jour de la vente
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#FEF9E7',
                      padding: '12px 14px',
                      borderRadius: '4px',
                      borderLeft: '2px solid #F5C842',
                    }}
                  >
                    <Typography
                      sx={{
                        margin: '0 0 8px 0',
                        fontSize: '13px',
                        lineHeight: '18px',
                        color: '#1a1a1a',
                        fontWeight: 600,
                      }}
                    >
                      ⚠️ À renseigner sur le CERFA :
                    </Typography>
                    <Typography
                      sx={{
                        margin: '0 0 6px 0',
                        fontSize: '13px',
                        lineHeight: '18px',
                        color: '#1a1a1a',
                      }}
                    >
                      • <strong>3 dates obligatoires</strong> : champs "certifié" + "fait à" (Ancien propriétaire) et
                      "fait à" (côté SKIPCAR)
                    </Typography>
                    <Typography
                      sx={{
                        margin: 0,
                        fontSize: '13px',
                        lineHeight: '18px',
                        color: '#1a1a1a',
                      }}
                    >
                      • <strong>Kilométrage exact au kilomètre près</strong>
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ marginTop: '1em', fontSize: '14px', lineHeight: '18px', color: '#1a1a1a' }}
                  >
                    <a href={cerfaExample} target="_blank" style={{ color: '#1a1a1a', textDecoration: 'underline' }}>
                      📄 Voir un exemple de CERFA complété
                    </a>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Document 2 - Carte grise */}
            <Box
              sx={{
                padding: '16px',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: '1px solid #e5e5e5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      margin: '0 0 8px 0',
                      fontSize: '15px',
                      color: '#1a1a1a',
                      fontWeight: 600,
                    }}
                  >
                    Carte grise
                  </Typography>
                  <Typography
                    sx={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#6a6a6a',
                      lineHeight: '20px',
                    }}
                  >
                    Barrée et signée
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Important Note */}
          <Box
            sx={{
              backgroundColor: '#f9f9f9',
              borderRadius: '6px',
              padding: '16px 20px',
              marginBottom: '32px',
            }}
          >
            <Typography
              sx={{
                margin: 0,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#4a4a4a',
              }}
            >
              <strong style={{ color: '#1a1a1a' }}>📋 À savoir :</strong> Le contrat Cashsentinel sera validé à
              réception des documents dûment complétés.
            </Typography>
          </Box>
        </DialogContent>
        <DialogContent sx={{ pt: 0 }}>
          <CfnPiecesWarning compact />
        </DialogContent>
        <Grid container item xs={12} md={12}>
          <Grid item xs={12} md={isStraightTradeIn ? 4 : 6}>
            <DialogTitle>Certificat de cession signée</DialogTitle>
            <DialogContent>
              <FirebaseUploadComponent
                onBeforeDelete={handleBeforeDelete}
                onDeleteComplete={(url) => handleDeleteComplete(url, 'certificateOfTransfer')}
                onUploadComplete={(url) => handleUploadComplete(url, 'certificateOfTransfer')}
                uploadedUrls={certificateOfTransferUrl}
              />
            </DialogContent>
          </Grid>
          <Grid item xs={12} md={isStraightTradeIn ? 4 : 6}>
            <DialogTitle>Carte grise barrée</DialogTitle>
            <DialogContent>
              <FirebaseUploadComponent
                onBeforeDelete={handleBeforeDelete}
                onDeleteComplete={(url) => handleDeleteComplete(url, 'barredVehicleRegistrationDocument')}
                onUploadComplete={(url) => handleUploadComplete(url, 'barredVehicleRegistrationDocument')}
                uploadedUrls={barredVehicleRegistrationDocumentUrl}
              />
            </DialogContent>
          </Grid>
          {isStraightTradeIn && (
            <Grid item xs={12} md={4}>
              <DialogTitle sx={{ fontSize: '16px' }}>Photos documents boîte à gant</DialogTitle>
              <DialogContent>
                <FirebaseUploadComponent
                  onBeforeDelete={handleBeforeDelete}
                  onDeleteComplete={(url) => handleDeleteComplete(url, 'gloveBoxDocuments')}
                  onUploadComplete={(url) => handleUploadComplete(url, 'gloveBoxDocuments')}
                  uploadedUrls={gloveBoxDocumentsUrl}
                />
              </DialogContent>
            </Grid>
          )}
          <DialogActions style={{ justifyContent: 'flex-end', width: '100%', flexDirection: 'row' }}>
            <Button className="secondaryDealActionButton" onClick={handleClose}>
              Annuler
            </Button>
            <Tooltip
              title="Vous ne pourrez pas valider tant que les fichiers ne seront pas envoyés"
              placement="top"
              disableHoverListener={!isValidationButtonDisabled}
            >
              <span style={{ marginRight: '30px' }}>
                <Button
                  className="dealActionButton"
                  onClick={handleOpenConfirmModal}
                  variant="contained"
                  color="primary"
                  disabled={isValidationButtonDisabled}
                >
                  Valider les fichiers
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </Grid>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={confirmModalOpen} onClose={handleCloseConfirmModal} fullWidth maxWidth="md">
        <DialogTitle>Confirmation de validation</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
              ⚠️ Merci de confirmer que le véhicule a bien fait l'objet d'un essai routier.
            </Typography>

            {damagesList && damagesList.length > 0 ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Éléments de remise en état déclarés :
                </Typography>
                <ul style={{ marginTop: 0 }}>
                  {damagesList.map((damage, index) => (
                    <li key={index}>
                      <Typography variant="body2">{damage}</Typography>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Vous n'avez déclaré AUCUN frais de remise en état sur ce véhicule.
              </Typography>
            )}

            <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 'bold', mt: 2 }}>
              ⚠️ Attention : En validant ce dossier, vous vous engagez sur le fait que les seuls éléments de remise en
              état sont ceux listés ci-dessus. Toute divergence pourra entraîner une révision du prix d'achat.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 3 }}>
              <Checkbox checked={checkboxAccepted} onChange={(e) => setCheckboxAccepted(e.target.checked)} />
              <Typography variant="body2" sx={{ ml: 1, mt: 1 }}>
                Je confirme que le véhicule a fait l'objet d'un essai routier et que les informations ci-dessus sont
                exactes.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmModal} className="secondaryDealActionButton">
            Annuler
          </Button>
          <Button
            onClick={handleFinalValidation}
            className="dealActionButton"
            variant="contained"
            disabled={!checkboxAccepted || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Accepter et valider'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadModal;
